import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Hammer, 
  ShieldCheck, 
  Zap, 
  Mail, 
  Phone,
  Menu,
  X,
  Upload,
  CheckCircle,
  Clock,
  Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import SEO from '@/components/SEO';
import usePageTracking from '@/hooks/usePageTracking';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration constants
const POLLING_INTERVAL_MS = 3000;
const REQUEST_TIMEOUT_MS = 30000;
const ANIMATION_OFFSET = 30; // Vertical offset for fade-in animations

// Animation constants to prevent inline objects
const FADE_IN_UP = { opacity: 0, y: ANIMATION_OFFSET };
const FADE_IN = { opacity: 0 };
const VISIBLE = { opacity: 1, y: 0 };
const VISIBLE_OPACITY = { opacity: 1 };
const SCALE_IN = { opacity: 0, scale: 0.9 };
const SCALE_VISIBLE = { opacity: 1, scale: 1 };
const CARD_FADE_UP = { opacity: 0, y: 20 };

// Service data with unique IDs
const SERVICES = [
  { id: 'service-1', icon: ShieldCheck, title: 'Car-Ports & Structures', desc: 'Durable carports and steel structures' },
  { id: 'service-2', icon: Hammer, title: 'Gates & Fences', desc: 'Custom gates and security fencing' },
  { id: 'service-3', icon: Zap, title: 'Railings & Balustrades', desc: 'Modern railings and staircases' },
  { id: 'service-4', icon: ShieldCheck, title: 'Custom Fabrication', desc: 'Braais, cages, and bespoke metalwork' }
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0A0B10]/80 border-b border-[#262A3B]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-3" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_fab-design-studio/artifacts/2crpr6n6_IMG-20230808-WA0012.jpg" 
              alt="En-Taro Arc Logo" 
              className="h-16 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-300 hover:text-[#D4AF37] transition-colors duration-200" data-testid="nav-home">HOME</Link>
            <Link to="/catalog" className="text-gray-300 hover:text-[#D4AF37] transition-colors duration-200" data-testid="nav-catalog">CATALOG</Link>
            <Link to="/custom" className="text-gray-300 hover:text-[#D4AF37] transition-colors duration-200" data-testid="nav-custom">CUSTOM FABRICATION</Link>
            <Link to="/admin" className="text-gray-300 hover:text-[#D4AF37] transition-colors duration-200" data-testid="nav-admin">ADMIN</Link>
            <a href="tel:+27710059409" className="text-[#00E5FF]" data-testid="nav-phone">+27 71 005 9409</a>
          </div>

          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden text-white" data-testid="nav-menu-toggle">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden py-4 space-y-4" data-testid="mobile-menu">
            <Link to="/" className="block text-gray-300" onClick={() => setIsOpen(false)}>HOME</Link>
            <Link to="/catalog" className="block text-gray-300" onClick={() => setIsOpen(false)}>CATALOG</Link>
            <Link to="/custom" className="block text-gray-300" onClick={() => setIsOpen(false)}>CUSTOM FABRICATION</Link>
            <Link to="/admin" className="block text-gray-300" onClick={() => setIsOpen(false)}>ADMIN</Link>
          </div>
        )}
      </div>
    </nav>
  );
};

const ServiceCard = ({ service, index }) => {
  const Icon = service.icon;
  const animationDelay = useMemo(() => ({ duration: 0.5, delay: index * 0.1 }), [index]);
  
  return (
    <motion.div
      key={service.id}
      initial={CARD_FADE_UP}
      whileInView={VISIBLE}
      transition={animationDelay}
      className="steel-card p-8 hover:border-[#D4AF37] transition-colors duration-200"
      data-testid={`service-card-${index}`}
    >
      <div className="text-[#D4AF37] mb-4"><Icon className="w-12 h-12" /></div>
      <h3 className="text-xl font-bold mb-2 uppercase" style={{fontFamily: 'Bebas Neue'}}>{service.title}</h3>
      <p className="text-gray-400 text-sm">{service.desc}</p>
    </motion.div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const heroAnimation = useMemo(() => ({ duration: 0.8 }), []);
  const subtitleAnimation = useMemo(() => ({ duration: 0.8, delay: 0.3 }), []);
  const ctaAnimation = useMemo(() => ({ duration: 0.8, delay: 0.6 }), []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO 
        title="En-Taro Arc Specialized Steel Works | Steel Fabrication Johannesburg"
        description="Professional steel fabrication in Johannesburg, Gauteng. Custom car ports, security gates, railings, braais, bird cages & metalwork. AI-powered blueprint generation."
        path="/"
      />
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source src="https://customer-assets.emergentagent.com/job_fab-design-studio/artifacts/blhupwzv_FIRE%20pit%20clip.mp4" type="video/mp4" />
        </video>
        <div className="hero-video-overlay"></div>
        <div className="hero-content text-center px-6 max-w-5xl">
          <motion.h1 
            initial={FADE_IN_UP}
            animate={VISIBLE}
            transition={heroAnimation}
            className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-normal uppercase mb-6"
            style={{fontFamily: 'Bebas Neue'}}
            data-testid="hero-title"
          >
            FORGED IN FIRE. <span className="text-[#D4AF37]">ENGINEERED FOR YOU.</span>
          </motion.h1>
          <motion.p 
            initial={FADE_IN}
            animate={VISIBLE_OPACITY}
            transition={subtitleAnimation}
            className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto"
            data-testid="hero-subtitle"
          >
            Specialized steel fabrication for car ports, gates, railings, cages, and custom metalwork.
          </motion.p>
          <motion.div 
            initial={FADE_IN}
            animate={VISIBLE_OPACITY}
            transition={ctaAnimation}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button 
              onClick={() => navigate('/custom')}
              className="btn-primary"
              data-testid="hero-cta-custom"
            >
              CREATE CUSTOM DESIGN
            </button>
            <button 
              onClick={() => navigate('/catalog')}
              className="btn-secondary"
              data-testid="hero-cta-catalog"
            >
              VIEW CATALOG
            </button>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-6 bg-[#0A0B10]" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl sm:text-5xl font-bold text-center mb-4 tracking-wide uppercase" style={{fontFamily: 'Bebas Neue'}} data-testid="services-title">
            WHAT WE DO
          </h2>
          <p className="text-center text-gray-400 mb-12 tracking-widest uppercase text-sm">SPECIALIZED STEEL FABRICATION</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((service, idx) => (
              <ServiceCard key={service.id} service={service} index={idx} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-[#13151D]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6 tracking-wide uppercase" style={{fontFamily: 'Bebas Neue'}} data-testid="cta-title">
            BRING YOUR VISION TO LIFE
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            Submit your design idea or upload reference images. Our AI-powered system will generate a professional blueprint for your custom steel fabrication project.
          </p>
          <button 
            onClick={() => navigate('/custom')}
            className="btn-primary text-xl"
            data-testid="cta-button"
          >
            START YOUR PROJECT
          </button>
        </div>
      </section>

      <footer className="py-12 px-6 bg-[#0A0B10] border-t border-[#262A3B]" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_fab-design-studio/artifacts/2crpr6n6_IMG-20230808-WA0012.jpg" 
                  alt="En-Taro Arc Logo" 
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-gray-400 text-sm">Specialized Steel Works</p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-[#D4AF37]" style={{fontFamily: 'Bebas Neue'}}>CONTACT</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>+27 71 005 9409</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>entaroarc@gmail.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Johannesburg, Gauteng</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-[#D4AF37]" style={{fontFamily: 'Bebas Neue'}}>SERVICES</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Design & Manufacture</li>
                <li>Maintenance & Repairs</li>
                <li>Custom Fabrications</li>
                <li>Welding & Cutting</li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-[#262A3B] text-center">
            <div className="flex justify-center mb-4">
              <a href="https://websitelaunches.com/site/entaroarc.com" target="_blank" rel="noopener">
                <img
                  src="https://websitelaunches.com/badge/entaroarc.com.svg?theme=dark"
                  alt="Established online - Public launch record"
                  width="255"
                  height="55"
                />
              </a>
            </div>
            <p className="text-sm text-gray-500">© 2025 En-Taro Arc Specialized Steel Works. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Catalog = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []); // API, axios, toast are stable module constants; setState functions are stable per React

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Note: 'p' is a callback parameter in map/filter, not an external dependency
  // Linter incorrectly flags it. Dependencies are correct as [products] and [products, selectedCategory]
  const categories = useMemo(() => ['All', ...new Set(products.map(p => p.category))], [products]);
  
  const filteredProducts = useMemo(() => {
    if (selectedCategory === 'All') {
      return products;
    }
    return products.filter(p => p.category === selectedCategory);
  }, [products, selectedCategory]);

  const scaleAnimation = useMemo(() => ({ duration: 0.3 }), []);

  return (
    <div className="min-h-screen pt-24 pb-20 px-6" data-testid="catalog-page">
      <SEO 
        title="Steel Products Catalog | En-Taro Arc Specialized Steel Works"
        description="Browse our full range of steel products: car ports, security gates, railings, braais, bird cages & fences. Quality steel fabrication in Johannesburg with transparent ZAR pricing."
        path="/catalog"
      />
      <div className="max-w-7xl mx-auto">
        <h1 className="text-5xl sm:text-6xl font-bold mb-4 tracking-wide uppercase" style={{fontFamily: 'Bebas Neue'}} data-testid="catalog-title">
          CUSTOM FABRICATIONS
        </h1>
        <p className="text-gray-400 mb-12">Pricing subject to change according to design and current steel rates • Premium quality guaranteed.</p>

        <div className="flex flex-wrap gap-4 mb-12" data-testid="category-filters">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-6 py-2 border transition-colors duration-200 ${
                selectedCategory === cat 
                  ? 'bg-[#D4AF37] text-black border-[#D4AF37]' 
                  : 'bg-transparent text-white border-[#262A3B] hover:border-[#D4AF37]'
              }`}
              style={{fontFamily: 'Bebas Neue', letterSpacing: '1px'}}
              data-testid={`category-filter-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20" data-testid="catalog-loading">
            <div className="animate-spin w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="products-grid">
            {filteredProducts.map(product => (
              <motion.div
                key={product.id}
                initial={SCALE_IN}
                animate={SCALE_VISIBLE}
                transition={scaleAnimation}
                className="steel-card overflow-hidden group"
                data-testid={`product-card-${product.id}`}
              >
                <div className="aspect-video bg-[#1A1D27] overflow-hidden">
                  <img 
                    src={product.image_url || 'https://images.pexels.com/photos/177872/pexels-photo-177872.jpeg'} 
                    alt={product.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="text-xs text-[#00E5FF] mb-2 tracking-widest uppercase">{product.category}</div>
                  <h3 className="text-2xl font-bold mb-2 uppercase" style={{fontFamily: 'Bebas Neue'}}>{product.name}</h3>
                  <p className="text-sm text-gray-400 mb-4">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-500">Base Weight: {product.base_weight_kg}kg</div>
                      <div className="text-2xl font-bold text-[#D4AF37]" style={{fontFamily: 'Bebas Neue'}}>R{product.price.toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        
        {/* Disclaimer Section */}
        <div className="mt-16 pt-8 border-t border-[#262A3B]">
          <h3 className="text-xl font-bold mb-4 text-[#D4AF37] uppercase" style={{fontFamily: 'Bebas Neue'}}>Terms & Conditions</h3>
          <div className="text-sm text-gray-400 space-y-3 leading-relaxed">
            <p>All material, consumables, presentation and structure costs are payable upfront and are for the sole cost of the client.</p>
            <p>Material Deposit costs must be received by the Service Provider at least 72 hours prior to work commencement.</p>
            <p>Where Ad Hoc Work is requested while on site, it will be billed separately and included in final invoicing.</p>
            <p>In the event that service provision periods exceed 7 days, invoices will be issued weekly and are payable on presentation.</p>
            <p>Where services provided are less than 7 days, invoices will be presented on completion and are payable on presentation.</p>
            <p>50% of Labour Costs are payable upfront, prior to job commencement.</p>
            <p>Quoted prices are based on estimated times only and may well vary dependent upon areas where travel is required or where additional unforeseen items/services are requested.</p>
            <p>Work will only commence once signed and/or e-mailed acceptance of quotation has been received by the Service Provider and once deposit terms have been met.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomFabrication = () => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    design_prompt: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [requestStatus, setRequestStatus] = useState(null);

  const pollRequestStatus = useCallback(async (id) => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/custom-requests/${id}`);
        setRequestStatus(response.data);
        
        if (response.data.status === 'completed') {
          clearInterval(interval);
          setSubmitting(false);
          toast.success('Blueprint ready!');
        } else if (response.data.status === 'failed') {
          clearInterval(interval);
          setSubmitting(false);
          toast.error('Blueprint generation failed');
        }
      } catch (error) {
        clearInterval(interval);
        setSubmitting(false);
      }
    }, POLLING_INTERVAL_MS);
  }, []); // API, axios, toast, POLLING_INTERVAL_MS are stable; setState functions are stable per React

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/custom-requests`, formData);
      setRequestId(response.data.id);
      toast.success('Request submitted! Processing your blueprint...');
      pollRequestStatus(response.data.id);
    } catch (error) {
      toast.error('Failed to submit request');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-6" data-testid="custom-fabrication-page">
      <SEO 
        title="Custom Fabrication & AI Blueprints | En-Taro Arc Steel Works"
        description="Get custom steel fabrication blueprints instantly. AI-powered design generation for gates, carports, braais & metalwork. Secure EFT payment. Johannesburg-based service."
        path="/custom"
      />
      <div className="max-w-5xl mx-auto">
        <h1 className="text-5xl sm:text-6xl font-bold mb-4 tracking-wide uppercase" style={{fontFamily: 'Bebas Neue'}} data-testid="custom-title">
          CUSTOM FABRICATION
        </h1>
        <p className="text-gray-400 mb-12">Describe your project and get an AI-generated blueprint</p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="steel-card p-8" data-testid="custom-form-container">
            <h2 className="text-2xl font-bold mb-6 uppercase" style={{fontFamily: 'Bebas Neue'}}>PROJECT DETAILS</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-bold mb-2 text-[#D4AF37] tracking-widest uppercase">Your Name</label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  required
                  className="bg-[#0A0B10] border-[#262A3B] text-white focus:border-[#D4AF37]"
                  data-testid="input-customer-name"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2 text-[#D4AF37] tracking-widest uppercase">Email</label>
                <Input
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                  required
                  className="bg-[#0A0B10] border-[#262A3B] text-white focus:border-[#D4AF37]"
                  data-testid="input-customer-email"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2 text-[#D4AF37] tracking-widest uppercase">Phone (Optional)</label>
                <Input
                  type="tel"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
                  className="bg-[#0A0B10] border-[#262A3B] text-white focus:border-[#D4AF37]"
                  data-testid="input-customer-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2 text-[#D4AF37] tracking-widest uppercase">Design Description</label>
                <Textarea
                  value={formData.design_prompt}
                  onChange={(e) => setFormData({...formData, design_prompt: e.target.value})}
                  required
                  rows={6}
                  placeholder="Describe your custom steel fabrication project in detail. Include dimensions, materials, style preferences, and any specific requirements."
                  className="bg-[#0A0B10] border-[#262A3B] text-white focus:border-[#D4AF37]"
                  data-testid="textarea-design-prompt"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold py-6 text-lg"
                style={{fontFamily: 'Bebas Neue', letterSpacing: '1px'}}
                data-testid="submit-custom-request"
              >
                {submitting ? 'GENERATING BLUEPRINT...' : 'SUBMIT PROJECT'}
              </Button>
            </form>
          </div>

          <div className="space-y-6">
            <div className="steel-card p-8" data-testid="blueprint-status-container">
              <h2 className="text-2xl font-bold mb-6 uppercase" style={{fontFamily: 'Bebas Neue'}}>BLUEPRINT STATUS</h2>
              
              {!requestStatus ? (
                <div className="text-center py-12 text-gray-500" data-testid="status-waiting">
                  <Upload className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Submit your project to generate a blueprint</p>
                </div>
              ) : (
                <div className="space-y-6" data-testid="status-active">
                  <div className="flex items-center space-x-4">
                    {requestStatus.status === 'processing' && (
                      <Clock className="w-8 h-8 text-[#00E5FF] animate-pulse" data-testid="status-processing-icon" />
                    )}
                    {requestStatus.status === 'completed' && (
                      <CheckCircle className="w-8 h-8 text-[#10B981]" data-testid="status-completed-icon" />
                    )}
                    <div>
                      <div className="text-sm text-gray-400">Status</div>
                      <div className="text-xl font-bold uppercase" style={{fontFamily: 'Bebas Neue'}} data-testid="status-text">
                        {requestStatus.status}
                      </div>
                    </div>
                  </div>

                  {requestStatus.status === 'completed' && (
                    <div className="space-y-4" data-testid="blueprint-ready">
                      <div className="p-4 bg-[#10B981]/10 border border-[#10B981] text-[#10B981]">
                        <p className="font-bold">✓ Blueprint generated successfully!</p>
                        <p className="text-sm mt-1">A copy has been sent to the owner&apos;s email.</p>
                      </div>

                      <div className="p-4 bg-[#1A1D27] border border-[#262A3B]">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400">Download Blueprint PDF</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl font-bold text-[#D4AF37]" style={{fontFamily: 'Bebas Neue'}}>
                              {requestStatus.payment_details?.amount || 'R250.00'}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mb-4">Payment via EFT required for download</p>
                        
                        {requestStatus.payment_details && (
                          <>
                            <div className="bg-[#D4AF37] text-black p-4 mb-4 border-2 border-[#E5C158]">
                              <div className="text-center">
                                <div className="text-xs font-bold uppercase tracking-widest mb-1">Your EFT Payment Reference</div>
                                <div className="text-2xl font-bold tracking-wider" style={{fontFamily: 'Bebas Neue'}} data-testid="payment-reference">
                                  {requestStatus.payment_details.reference}
                                </div>
                                <div className="text-xs mt-2 font-semibold">⚠️ Use this reference when making payment</div>
                              </div>
                            </div>
                            
                            <div className="bg-[#0A0B10] p-4 space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-400">Bank:</span>
                                <span className="text-white font-semibold">{requestStatus.payment_details.bank}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Account Name:</span>
                                <span className="text-white font-semibold">{requestStatus.payment_details.account_name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Account Number:</span>
                                <span className="text-white font-semibold">{requestStatus.payment_details.account_number}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Branch Code:</span>
                                <span className="text-white font-semibold">{requestStatus.payment_details.branch_code}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Account Type:</span>
                                <span className="text-white font-semibold">{requestStatus.payment_details.account_type}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-400">Payment Reference:</span>
                                <span className="text-[#D4AF37] font-bold">{requestStatus.payment_details.reference}</span>
                              </div>
                              <div className="flex justify-between border-t border-[#262A3B] pt-2 mt-2">
                                <span className="text-gray-400">Amount to Pay:</span>
                                <span className="text-[#D4AF37] font-bold text-lg">{requestStatus.payment_details.amount}</span>
                              </div>
                            </div>
                          </>
                        )}
                        
                        <div className="mt-4 p-3 bg-[#00E5FF]/10 border border-[#00E5FF]">
                          <p className="text-xs text-[#00E5FF] font-semibold mb-2">📧 Payment Instructions:</p>
                          <ol className="text-xs text-gray-300 space-y-1 ml-4 list-decimal">
                            <li>Make EFT payment using the details above</li>
                            <li><strong>Include the payment reference</strong> in your EFT description</li>
                            <li>Email proof of payment to <span className="text-[#00E5FF] font-semibold">entaroarc@gmail.com</span></li>
                            <li>Include your name: <span className="text-white font-semibold">{requestStatus.customer_name}</span></li>
                            <li>We&apos;ll send your blueprint within 24 hours of payment confirmation</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="steel-card p-6">
              <h3 className="text-lg font-bold mb-4 text-[#00E5FF] uppercase" style={{fontFamily: 'Bebas Neue'}}>HOW IT WORKS</h3>
              <div className="space-y-3 text-sm text-gray-400">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#D4AF37] text-black flex items-center justify-center font-bold flex-shrink-0">1</div>
                  <p>Describe your custom steel fabrication project in detail</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#D4AF37] text-black flex items-center justify-center font-bold flex-shrink-0">2</div>
                  <p>AI analyzes your requirements and generates technical specifications</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#D4AF37] text-black flex items-center justify-center font-bold flex-shrink-0">3</div>
                  <p>Professional blueprint is created and emailed to our team</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#D4AF37] text-black flex items-center justify-center font-bold flex-shrink-0">4</div>
                  <p>Pay R250 via EFT to download your blueprint PDF</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = ({ stats, steelPrice, setSteelPrice, updateSteelPrice }) => (
  <div className="space-y-8" data-testid="dashboard-content">
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      <div className="steel-card p-6" data-testid="stat-requests">
        <div className="text-sm text-gray-400 mb-2">Total Requests</div>
        <div className="text-4xl font-bold text-[#D4AF37]" style={{fontFamily: 'Bebas Neue'}}>{stats.total_requests}</div>
      </div>
      <div className="steel-card p-6" data-testid="stat-pending">
        <div className="text-sm text-gray-400 mb-2">Pending</div>
        <div className="text-4xl font-bold text-[#00E5FF]" style={{fontFamily: 'Bebas Neue'}}>{stats.pending_requests}</div>
      </div>
      <div className="steel-card p-6" data-testid="stat-products">
        <div className="text-sm text-gray-400 mb-2">Products</div>
        <div className="text-4xl font-bold text-white" style={{fontFamily: 'Bebas Neue'}}>{stats.total_products}</div>
      </div>
      <div className="steel-card p-6" data-testid="stat-steel-price">
        <div className="text-sm text-gray-400 mb-2">Steel Price (R/kg)</div>
        <div className="text-4xl font-bold text-[#10B981]" style={{fontFamily: 'Bebas Neue'}}>R{stats.current_steel_price}</div>
      </div>
    </div>

    <div className="steel-card p-8" data-testid="steel-price-manager">
      <h2 className="text-2xl font-bold mb-6 uppercase" style={{fontFamily: 'Bebas Neue'}}>UPDATE STEEL PRICE (ZAR per kg)</h2>
      <div className="flex space-x-4">
        <Input
          type="number"
          step="0.01"
          value={steelPrice}
          onChange={(e) => setSteelPrice(e.target.value)}
          placeholder="Price per kg (ZAR)"
          className="bg-[#0A0B10] border-[#262A3B] text-white focus:border-[#D4AF37]"
          data-testid="steel-price-input"
        />
        <Button
          onClick={updateSteelPrice}
          className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold"
          style={{fontFamily: 'Bebas Neue', letterSpacing: '1px'}}
          data-testid="update-steel-price-button"
        >
          UPDATE
        </Button>
      </div>
      <p className="text-xs text-gray-500 mt-4">This will automatically update all product prices in the catalog</p>
    </div>
  </div>
);

const AdminRequests = ({ requests }) => (
  <div className="space-y-4" data-testid="requests-content">
    {requests.length === 0 ? (
      <div className="steel-card p-12 text-center text-gray-500" data-testid="no-requests">
        <p>No custom requests yet</p>
      </div>
    ) : (
      requests.map(req => {
        let statusColor = 'bg-gray-600 text-white';
        if (req.status === 'completed') {
          statusColor = 'bg-[#10B981] text-black';
        } else if (req.status === 'processing') {
          statusColor = 'bg-[#00E5FF] text-black';
        }

        return (
          <div key={req.id} className="steel-card p-6" data-testid={`request-item-${req.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold" style={{fontFamily: 'Bebas Neue'}}>{req.customer_name}</h3>
                <p className="text-sm text-gray-400">{req.customer_email}</p>
              </div>
              <div className={`px-4 py-1 text-sm font-bold ${statusColor}`} style={{fontFamily: 'Bebas Neue'}}>
                {req.status}
              </div>
            </div>
            <p className="text-sm text-gray-300 mb-4">{req.design_prompt}</p>
            <div className="text-xs text-gray-500">
              {new Date(req.created_at).toLocaleString()}
            </div>
          </div>
        );
      })
    )}
  </div>
);

const Admin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [stats, setStats] = useState(null);
  const [steelPrice, setSteelPrice] = useState('');
  const [requests, setRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');

  const fetchAdminData = useCallback(async () => {
    try {
      const [statsRes, requestsRes, priceRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/custom-requests`),
        axios.get(`${API}/steel-price`)
      ]);
      setStats(statsRes.data);
      setRequests(requestsRes.data);
      setSteelPrice(priceRes.data.price_per_kg);
    } catch (error) {
      toast.error('Failed to fetch admin data');
    }
  }, []); // API, axios, toast are stable module constants

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsLoggedIn(true);
      toast.success('Logged in successfully');
      fetchAdminData();
    } catch (error) {
      toast.error('Invalid password');
    }
  };

  const updateSteelPrice = useCallback(async () => {
    try {
      await axios.put(`${API}/steel-price`, { price_per_kg: parseFloat(steelPrice) });
      toast.success('Steel price updated');
      fetchAdminData();
    } catch (error) {
      toast.error('Failed to update price');
    }
  }, [steelPrice, fetchAdminData]); // API, axios, toast are stable module constants

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-6 flex items-center justify-center" data-testid="admin-login-page">
        <SEO 
          title="Admin Dashboard | En-Taro Arc Steel Works"
          description="Admin access for En-Taro Arc Steel Works. Manage steel prices, view custom requests, and monitor business operations."
          path="/admin"
        />
        <div className="steel-card p-8 max-w-md w-full">
          <div className="flex items-center justify-center mb-8">
            <Settings className="w-12 h-12 text-[#D4AF37]" />
          </div>
          <h1 className="text-3xl font-bold text-center mb-8 uppercase" style={{fontFamily: 'Bebas Neue'}} data-testid="admin-login-title">ADMIN ACCESS</h1>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-bold mb-2 text-[#D4AF37] tracking-widest uppercase">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-[#0A0B10] border-[#262A3B] text-white focus:border-[#D4AF37]"
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold py-4"
              style={{fontFamily: 'Bebas Neue', letterSpacing: '1px'}}
              data-testid="admin-login-button"
            >
              LOGIN
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-6" data-testid="admin-dashboard-page">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-5xl font-bold mb-8 tracking-wide uppercase" style={{fontFamily: 'Bebas Neue'}} data-testid="admin-dashboard-title">ADMIN DASHBOARD</h1>

        <div className="flex space-x-4 mb-8 border-b border-[#262A3B]" data-testid="admin-tabs">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`pb-4 px-6 font-bold transition-colors duration-200 ${
              activeTab === 'dashboard' ? 'border-b-2 border-[#D4AF37] text-[#D4AF37]' : 'text-gray-400'
            }`}
            style={{fontFamily: 'Bebas Neue', letterSpacing: '1px'}}
            data-testid="tab-dashboard"
          >
            DASHBOARD
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`pb-4 px-6 font-bold transition-colors duration-200 ${
              activeTab === 'requests' ? 'border-b-2 border-[#D4AF37] text-[#D4AF37]' : 'text-gray-400'
            }`}
            style={{fontFamily: 'Bebas Neue', letterSpacing: '1px'}}
            data-testid="tab-requests"
          >
            REQUESTS
          </button>
        </div>

        {activeTab === 'dashboard' && stats && (
          <AdminDashboard 
            stats={stats} 
            steelPrice={steelPrice} 
            setSteelPrice={setSteelPrice}
            updateSteelPrice={updateSteelPrice}
          />
        )}

        {activeTab === 'requests' && (
          <AdminRequests requests={requests} />
        )}
      </div>
    </div>
  );
};

// Component to track page views (must be inside BrowserRouter)
const PageTracker = () => {
  usePageTracking();
  return null;
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <PageTracker />
          <Navigation />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/custom" element={<CustomFabrication />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
